import React from 'react';
import { flushSync } from 'react-dom';
import { useSnackbar } from 'pounceV2';
import { useGenerateAIAttachmentUploadUrl } from '../graphql/generateAIAttachmentUploadUrl.generated';
import {
  isBinaryFile,
  isAllowedFileType,
  MAX_ATTACHMENTS,
  MAX_BINARY_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_BYTES,
} from '../utils/attachmentConstants';

export interface Attachment {
  id: string;
  fileName: string;
  s3Url: string;
  isUploading: boolean;
}

interface UseAIAttachmentsReturn {
  attachments: Attachment[];
  attachmentUrls: string[];
  addAttachment: (file: File) => void;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
  canAttach: boolean;
  isUploading: boolean;
}

export const useAIAttachments = (): UseAIAttachmentsReturn => {
  const [attachments, setAttachments] = React.useState<Attachment[]>([]);
  const [generateUploadUrl] = useGenerateAIAttachmentUploadUrl();
  const { pushSnackbar } = useSnackbar();

  // Track mount state so in-flight uploads silently bail out after unmount
  // instead of calling setState / pushSnackbar on a torn-down tree.
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isUploading = React.useMemo(() => attachments.some(a => a.isUploading), [attachments]);
  const canAttach = React.useMemo(() => attachments.length < MAX_ATTACHMENTS, [attachments]);

  const attachmentUrls = React.useMemo(
    () => attachments.filter(a => !a.isUploading).map(a => a.s3Url),
    [attachments],
  );

  const addAttachment = React.useCallback(
    async (file: File) => {
      // Synchronous validation — runs before any async work and before the
      // component could have unmounted, so no mountedRef check needed.
      if (!isAllowedFileType(file)) {
        pushSnackbar({
          variant: 'error',
          title: 'Unsupported file type',
          description: `${file.name} has an unsupported file type (${file.type || 'unknown'}).`,
        });
        return;
      }

      const maxSize = isBinaryFile(file) ? MAX_BINARY_FILE_SIZE_BYTES : MAX_FILE_SIZE_BYTES;
      if (file.size > maxSize) {
        const limitMB = maxSize / (1024 * 1024);
        pushSnackbar({
          variant: 'error',
          title: 'File too large',
          description: `${file.name} exceeds the ${limitMB} MB limit.`,
        });
        return;
      }

      const id = crypto.randomUUID();

      // Everything below is wrapped in a single try/catch so that errors from
      // flushSync (e.g. a concurrent render conflict when dropping many files)
      // never escape as unhandled promise rejections — addAttachment is called
      // without `await` from forEach, so any throw becomes an unhandled rejection
      // that crashes the app via the ErrorBoundary.
      try {
        // flushSync forces the updater to run synchronously so `rejected` is set
        // before we read it. Without this, React 18 automatic batching defers the
        // updater to the render phase — when multiple files are dropped via forEach,
        // all calls see `rejected === false` and proceed to generate presigned URLs
        // and upload to S3, orphaning the excess objects.
        let rejected = false;
        flushSync(() => {
          setAttachments(prev => {
            if (prev.length >= MAX_ATTACHMENTS) {
              rejected = true;
              return prev;
            }
            return [...prev, { id, fileName: file.name, s3Url: '', isUploading: true }];
          });
        });
        if (rejected) {
          pushSnackbar({
            variant: 'error',
            title: 'Too many attachments',
            description: `You can attach up to ${MAX_ATTACHMENTS} files.`,
          });
          return;
        }

        // Use the browser's File.type for reliable MIME detection — the presigned URL
        // enforces this Content-Type so S3 stores the correct metadata for later retrieval.
        const contentType = file.type || 'application/octet-stream';

        // Get presigned URL locked to this content type
        const { data } = await generateUploadUrl({ variables: { contentType } });
        if (!mountedRef.current) {
          return;
        }
        if (!data) {
          throw new Error('Failed to generate upload URL');
        }

        const { uploadUrl, s3Url } = data.generateAIAttachmentUploadUrl;

        // Upload file to S3 — Content-Type must match the presigned URL's enforced type
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
        if (!mountedRef.current) {
          return;
        }

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        // Mark as complete
        setAttachments(prev =>
          prev.map(a => (a.id === id ? { ...a, s3Url, isUploading: false } : a)),
        );
      } catch (err) {
        // Remove failed attachment placeholder and notify the user.
        // After unmount, silently bail — the component tree is gone.
        // eslint-disable-next-line no-console
        console.error('Attachment upload failed:', err);
        if (!mountedRef.current) {
          return;
        }
        setAttachments(prev => prev.filter(a => a.id !== id));
        pushSnackbar({
          variant: 'error',
          title: 'Attachment failed',
          description: `Failed to upload ${file.name}. Please try again.`,
        });
      }
    },
    [generateUploadUrl, pushSnackbar],
  );

  const removeAttachment = React.useCallback((id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearAttachments = React.useCallback(() => {
    setAttachments([]);
  }, []);

  return {
    attachments,
    attachmentUrls,
    addAttachment,
    removeAttachment,
    clearAttachments,
    canAttach,
    isUploading,
  };
};
